// One shared 1024² decal atlas for every robot, painted once at startup on a canvas (no asset
// bytes): faces (toaster grimace, microwave screen moods), stickers, graffiti, keypads, labels.
// Decal quads in models.ts sample it by cell; the print grunge then rusts over them.
// Grid: 8x8 cells of 128 px. A rect is [cellX, cellY, cellsW, cellsH] from the top-left.

import * as THREE from 'three';

export type Cell = readonly [number, number, number, number];

export const DC = {
  grimace: [0, 0, 4, 2],
  faceSmile: [4, 0, 2, 2],
  faceAngry: [6, 0, 2, 2],
  faceStatic: [0, 2, 2, 2],
  gulp: [2, 2, 1, 1],
  warn: [3, 2, 1, 1],
  barcode: [4, 2, 1, 1],
  asis: [5, 2, 1, 1],
  hanko: [6, 2, 1, 1],
  price: [7, 2, 1, 1],
  number: [2, 3, 1, 1],
  patch: [3, 3, 1, 1],
  sale: [4, 3, 2, 1],
  smiley: [6, 3, 1, 1],
  tagMag: [0, 4, 2, 1],
  tagCream: [2, 4, 2, 1],
  tagInk: [4, 4, 2, 1],
  keypad: [6, 4, 1, 2],
  vent: [7, 4, 1, 1],
  grille: [7, 5, 1, 1],
  dial: [0, 5, 1, 1],
  stripes: [1, 5, 2, 1],
  copyPanel: [3, 5, 2, 1],
  plate: [5, 5, 1, 1],
  rustBloom: [0, 6, 2, 2],
  collage: [2, 6, 2, 2],
  scrawl: [4, 6, 2, 1],
  arrow: [6, 6, 1, 1],
  bolt: [7, 6, 1, 1],
  spray: [7, 3, 1, 1],
  tape: [4, 7, 2, 1],
  stencil: [6, 7, 2, 1],
} as const satisfies Record<string, Cell>;
export type DecalName = keyof typeof DC;

const S = 1024, C = 128;
let atlas: THREE.CanvasTexture | null = null;

export function decalAtlas(): THREE.CanvasTexture {
  if (atlas) return atlas;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const g = cv.getContext('2d')!;
  g.clearRect(0, 0, S, S);
  let seed = 3;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const INK = '#383019', CREAM = '#f0e6c8', PAPER = '#e6e0cd', TAN = '#d5c094', RED = '#d44f2b', PLUM = '#5e3a30',
    MAG = '#b03a6a', ORANGE = '#d9662b', BRICK = '#8e3b26', TEAL = '#3e5a4e', MINT = '#bdd2a2', RUST = '#a84a2a';
  const rect = (c: Cell) => [c[0] * C, c[1] * C, c[2] * C, c[3] * C] as const;
  /** a slightly ragged filled polygon (print edge) */
  const rag = (pts: number[][], fill: string, j = 1.2) => {
    g.fillStyle = fill;
    g.beginPath();
    pts.forEach((p, i) => { const x = p[0] + (rnd() - 0.5) * j, y = p[1] + (rnd() - 0.5) * j; if (i) g.lineTo(x, y); else g.moveTo(x, y); });
    g.closePath(); g.fill();
  };
  const ragRect = (x: number, y: number, w: number, h: number, fill: string, j = 1.5) => {
    const pts: number[][] = [];
    const n = 6;
    for (let i = 0; i <= n; i++) pts.push([x + (w * i) / n, y]);
    for (let i = 0; i <= n; i++) pts.push([x + w, y + (h * i) / n]);
    for (let i = n; i >= 0; i--) pts.push([x + (w * i) / n, y + h]);
    for (let i = n; i >= 0; i--) pts.push([x, y + (h * i) / n]);
    rag(pts, fill, j);
  };
  const text = (s: string, x: number, y: number, size: number, fill: string, rot = 0, font = 'bold') => {
    g.save(); g.translate(x, y); g.rotate(rot);
    g.font = `${font} ${size}px "DejaVu Sans", Arial, sans-serif`;
    g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillStyle = fill; g.fillText(s, 0, 0);
    g.restore();
  };
  const scuff = (x: number, y: number, w: number, h: number, n: number, col = 'rgba(56,48,25,0.25)') => {
    g.fillStyle = col;
    for (let i = 0; i < n; i++) g.fillRect(x + rnd() * w, y + rnd() * h, 1 + rnd() * 3, 1 + rnd() * 2);
  };
  const stroke = (pts: number[][], col: string, w: number) => {
    g.strokeStyle = col; g.lineWidth = w; g.lineCap = 'round'; g.lineJoin = 'round';
    g.beginPath(); pts.forEach((p, i) => (i ? g.lineTo(p[0], p[1]) : g.moveTo(p[0], p[1]))); g.stroke();
  };

  // ---- toaster grimace: a big downturned arc of dark mouth with jagged cream teeth
  {
    const [x, y, w, h] = rect(DC.grimace);
    const cx = x + w / 2;
    const arc = (t: number, off: number) => {
      const xx = cx + t * w * 0.47;
      const yy = y + h * 0.3 + (t * t) * h * 0.42 - off - Math.abs(t) * h * 0.04;
      return [xx, yy];
    };
    const top: number[][] = [], bot: number[][] = [];
    const N = 40;
    for (let i = 0; i <= N; i++) {
      const t = -1 + (2 * i) / N, th = (1 - t * t * 0.85);
      top.push(arc(t, h * 0.14 * th));
      bot.push(arc(t, -h * 0.12 * th));
    }
    rag([...top, ...bot.reverse()], PLUM, 0.6);
    bot.reverse();
    // teeth: upper row hangs down, lower row points up
    const T = 15;
    for (let i = 0; i < T; i++) {
      const t0 = -0.94 + (1.88 * i) / T, t1 = t0 + 1.88 / T, tm = (t0 + t1) / 2;
      const a = arc(t0, h * 0.14 * (1 - t0 * t0 * 0.85)), b = arc(t1, h * 0.14 * (1 - t1 * t1 * 0.85));
      const m = arc(tm, h * 0.14 * (1 - tm * tm * 0.85) * (0.05 + rnd() * 0.25));
      rag([a, b, [m[0], m[1] + 2]], i % 3 ? CREAM : '#e8c25a', 0.5);
      const c = arc(t0 + 0.06, -h * 0.12 * (1 - t0 * t0 * 0.85)), d = arc(t1 + 0.06, -h * 0.12 * (1 - t1 * t1 * 0.85));
      const n = arc(tm + 0.06, -h * 0.12 * (1 - tm * tm * 0.85) * (0.1 + rnd() * 0.3));
      rag([c, d, [n[0], n[1] - 2]], i % 4 ? CREAM : '#e8c25a', 0.5);
    }
    // ink outline of the lips
    stroke(top, INK, 4); stroke(bot, INK, 4);
  }

  // ---- microwave screen faces: dark-red frame, cream screen, ink features
  const screen = (c: Cell, mood: 0 | 1 | 2) => {
    const [x, y, w, h] = rect(c);
    ragRect(x + 6, y + 30, w - 12, h - 60, '#7a2e22', 1);
    ragRect(x + 20, y + 44, w - 40, h - 88, mood === 2 ? '#bfb7a0' : '#efe4c4', 1);
    const cx = x + w / 2, cy = y + h / 2;
    if (mood === 0) {
      ragRect(cx - 52, cy - 30, 18, 26, '#6a2a20', 0.6); ragRect(cx + 34, cy - 30, 18, 26, '#6a2a20', 0.6);
      const pts: number[][] = [];
      for (let i = 0; i <= 16; i++) { const t = -1 + i / 8; pts.push([cx + t * 62, cy + 22 + (1 - t * t) * 26]); }
      for (let i = 16; i >= 0; i--) { const t = -1 + i / 8; pts.push([cx + t * 54, cy + 22 + (1 - t * t) * 12]); }
      rag(pts, '#6a2a20', 0.6);
    } else if (mood === 1) {
      rag([[cx - 62, cy - 40], [cx - 22, cy - 20], [cx - 26, cy - 8], [cx - 64, cy - 26]], '#6a2a20', 0.5);
      rag([[cx + 62, cy - 40], [cx + 22, cy - 20], [cx + 26, cy - 8], [cx + 64, cy - 26]], '#6a2a20', 0.5);
      ragRect(cx - 50, cy - 8, 18, 20, '#6a2a20', 0.5); ragRect(cx + 32, cy - 8, 18, 20, '#6a2a20', 0.5);
      const pts: number[][] = [];
      for (let i = 0; i <= 16; i++) { const t = -1 + i / 8; pts.push([cx + t * 58, cy + 52 - (1 - t * t) * 22]); }
      for (let i = 16; i >= 0; i--) { const t = -1 + i / 8; pts.push([cx + t * 50, cy + 60 - (1 - t * t) * 14]); }
      rag(pts, '#6a2a20', 0.6);
    } else {
      for (let yy = y + 46; yy < y + h - 46; yy += 3) {
        for (let xx = x + 22; xx < x + w - 22; xx += 4) {
          const v = rnd();
          g.fillStyle = v > 0.55 ? '#2e2a22' : v > 0.3 ? '#8a8270' : '#e8e0c8';
          g.fillRect(xx, yy, 4, 3);
        }
      }
      ragRect(x + 20, cy - 6, w - 40, 10, '#e8e0c8', 0.5);
    }
  };
  screen(DC.faceSmile, 0); screen(DC.faceAngry, 1); screen(DC.faceStatic, 2);

  // ---- stickers
  {
    const [x, y] = rect(DC.gulp);
    g.fillStyle = CREAM; g.beginPath(); g.arc(x + 64, y + 64, 56, 0, Math.PI * 2); g.fill();
    g.strokeStyle = RED; g.lineWidth = 8; g.beginPath(); g.arc(x + 64, y + 64, 46, 0, Math.PI * 2); g.stroke();
    text('G.U.L.P.', x + 64, y + 58, 22, INK);
    text('ASSET', x + 64, y + 84, 14, RED);
    scuff(x + 10, y + 10, 108, 108, 60);
  }
  {
    const [x, y] = rect(DC.warn);
    rag([[x + 64, y + 10], [x + 120, y + 112], [x + 8, y + 112]], '#e3a21f', 1);
    rag([[x + 64, y + 30], [x + 104, y + 102], [x + 24, y + 102]], INK, 1);
    rag([[x + 64, y + 40], [x + 96, y + 97], [x + 32, y + 97]], '#e3a21f', 1);
    text('!', x + 64, y + 80, 44, INK);
  }
  {
    const [x, y] = rect(DC.barcode);
    ragRect(x + 8, y + 24, 112, 80, PAPER, 1);
    for (let i = 0; i < 26; i++) { const bw = 1 + Math.floor(rnd() * 4); g.fillStyle = INK; g.fillRect(x + 16 + i * 3.8, y + 32, bw, 52); }
    text('0451-SCRAP', x + 64, y + 94, 12, INK);
  }
  {
    const [x, y] = rect(DC.asis);
    ragRect(x + 6, y + 30, 116, 68, RED, 2);
    text('AS IS', x + 64, y + 66, 36, CREAM);
  }
  {
    const [x, y] = rect(DC.hanko);
    ragRect(x + 24, y + 20, 80, 88, '#c23a2a', 2);
    g.fillStyle = CREAM;
    for (let i = 0; i < 9; i++) g.fillRect(x + 34 + (i % 3) * 22 + rnd() * 6, y + 30 + Math.floor(i / 3) * 24 + rnd() * 6, 10 + rnd() * 8, 4 + rnd() * 10);
  }
  {
    const [x, y] = rect(DC.price);
    rag([[x + 10, y + 40], [x + 36, y + 14], [x + 118, y + 14], [x + 118, y + 66], [x + 36, y + 66]], TAN, 1);
    g.fillStyle = INK; g.beginPath(); g.arc(x + 30, y + 40, 6, 0, Math.PI * 2); g.fill();
    text('50¢', x + 78, y + 41, 30, INK);
    ragRect(x + 14, y + 80, 100, 34, CREAM, 1.5); text('FREE', x + 64, y + 98, 24, RED);
  }
  {
    const [x, y] = rect(DC.number);
    ragRect(x + 14, y + 18, 100, 92, CREAM, 1.5);
    text('07', x + 64, y + 66, 62, INK);
  }
  {
    const [x, y] = rect(DC.patch);
    ragRect(x + 10, y + 16, 108, 96, PAPER, 3);
    scuff(x + 10, y + 16, 108, 96, 120);
    stroke([[x + 20, y + 50], [x + 100, y + 46]], INK, 3); stroke([[x + 20, y + 70], [x + 80, y + 72]], INK, 3);
  }
  {
    const [x, y, w] = rect(DC.sale);
    ragRect(x + 8, y + 28, w - 16, 72, ORANGE, 2);
    text('SALE!', x + w / 2, y + 66, 52, CREAM);
  }
  {
    const [x, y] = rect(DC.smiley);
    g.fillStyle = '#e3a21f'; g.beginPath(); g.arc(x + 64, y + 64, 52, 0, Math.PI * 2); g.fill();
    g.fillStyle = INK; g.fillRect(x + 44, y + 40, 10, 22); g.fillRect(x + 74, y + 40, 10, 22);
    g.strokeStyle = INK; g.lineWidth = 8; g.beginPath(); g.arc(x + 64, y + 66, 30, 0.2, Math.PI - 0.2); g.stroke();
  }
  // ---- graffiti tags
  const tag = (c: Cell, col: string, w: number) => {
    const [x, y, ww, hh] = rect(c);
    for (let k = 0; k < 3; k++) {
      const pts: number[][] = [];
      let px = x + 16, py = y + hh / 2 + (rnd() - 0.5) * 30;
      for (let i = 0; i < 14; i++) {
        px += ww / 16; py = y + hh * (0.2 + rnd() * 0.6);
        pts.push([px, py]);
      }
      stroke(pts, col, w * (k ? 0.6 : 1));
    }
    // drips off the tag
    g.fillStyle = col;
    for (let i = 0; i < 6; i++) g.fillRect(x + 20 + rnd() * (ww - 40), y + hh * 0.6, 3, 10 + rnd() * 34);
  };
  tag(DC.tagMag, MAG, 9); tag(DC.tagCream, CREAM, 8); tag(DC.tagInk, INK, 7);
  {
    const [x, y, w, h] = rect(DC.scrawl);
    text('NO REFUNDS', x + w / 2, y + h / 2, 30, MAG, -0.08, 'bold italic');
  }
  {
    const [x, y] = rect(DC.arrow);
    rag([[x + 14, y + 52], [x + 70, y + 52], [x + 70, y + 26], [x + 116, y + 64], [x + 70, y + 102], [x + 70, y + 76], [x + 14, y + 76]], CREAM, 2);
  }
  {
    const [x, y] = rect(DC.bolt);
    rag([[x + 70, y + 8], [x + 30, y + 70], [x + 60, y + 70], [x + 46, y + 120], [x + 98, y + 50], [x + 66, y + 50], [x + 84, y + 8]], '#e3a21f', 1);
  }
  // ---- panels
  {
    const [x, y, w, h] = rect(DC.keypad);
    ragRect(x + 8, y + 8, w - 16, h - 16, '#d8ccac', 1);
    ragRect(x + 18, y + 18, w - 36, 40, '#2b3a33', 1);
    text('0:00', x + 64, y + 38, 22, '#e3a21f');
    for (let r = 0; r < 4; r++) for (let c = 0; c < 3; c++) ragRect(x + 22 + c * 30, y + 76 + r * 34, 22, 22, r === 3 && c === 2 ? RED : '#8a8270', 0.6);
    ragRect(x + 22, y + 220, 84, 18, BRICK, 0.6);
  }
  {
    const [x, y] = rect(DC.vent);
    for (let i = 0; i < 7; i++) ragRect(x + 14, y + 14 + i * 15, 100, 7, INK, 0.6);
  }
  {
    const [x, y] = rect(DC.grille);
    g.fillStyle = INK;
    for (let r = 0; r < 7; r++) for (let c = 0; c < 7; c++) { g.beginPath(); g.arc(x + 22 + c * 14, y + 22 + r * 14, 4, 0, Math.PI * 2); g.fill(); }
  }
  {
    const [x, y] = rect(DC.dial);
    g.fillStyle = PAPER; g.beginPath(); g.arc(x + 64, y + 64, 54, 0, Math.PI * 2); g.fill();
    g.strokeStyle = INK; g.lineWidth = 6; g.beginPath(); g.arc(x + 64, y + 64, 54, 0, Math.PI * 2); g.stroke();
    for (let i = 0; i < 10; i++) { const a = -2.4 + i * 0.53; stroke([[x + 64 + Math.cos(a) * 40, y + 64 + Math.sin(a) * 40], [x + 64 + Math.cos(a) * 50, y + 64 + Math.sin(a) * 50]], INK, 3); }
    stroke([[x + 64, y + 64], [x + 96, y + 40]], RED, 5);
  }
  {
    const [x, y, w] = rect(DC.stripes);
    for (let i = 0; i < 10; i++) rag([[x + i * 28, y + 100], [x + i * 28 + 14, y + 100], [x + i * 28 + 44, y + 28], [x + i * 28 + 30, y + 28]], i % 2 ? INK : '#e3a21f', 1);
    g.clearRect(x + w, y, 4, C);
  }
  {
    const [x, y, w, h] = rect(DC.copyPanel);
    ragRect(x + 6, y + 14, w - 12, h - 28, '#c9bf9f', 1);
    for (let i = 0; i < 6; i++) ragRect(x + 18 + i * 24, y + 30, 16, 16, i === 5 ? '#4d9a4a' : INK, 0.5);
    ragRect(x + 170, y + 26, 70, 30, TEAL, 0.5);
    text('COPY', x + 205, y + 92, 20, INK);
    ragRect(x + 18, y + 64, 120, 10, ORANGE, 0.5);
  }
  {
    const [x, y] = rect(DC.plate);
    ragRect(x + 8, y + 34, 112, 60, '#c08a3e', 1);
    g.fillStyle = INK; for (const px of [16, 112]) for (const py of [42, 86]) { g.beginPath(); g.arc(x + px, y + py, 4, 0, Math.PI * 2); g.fill(); }
    text('MODEL 7', x + 64, y + 64, 18, INK);
  }
  // ---- rust bloom + drip cluster (used under seams / on the dirtiest panels)
  {
    const [x, y, w, h] = rect(DC.rustBloom);
    for (let i = 0; i < 260; i++) {
      const a = rnd() * Math.PI * 2, r = Math.pow(rnd(), 0.7) * w * 0.42;
      g.fillStyle = i % 3 ? RUST : '#7a3a22';
      g.globalAlpha = 0.5 + rnd() * 0.5;
      g.beginPath(); g.arc(x + w / 2 + Math.cos(a) * r, y + h / 2 + Math.sin(a) * r * 0.8, 2 + rnd() * 8, 0, Math.PI * 2); g.fill();
    }
    g.globalAlpha = 1;
  }
  // ---- a sticker collage: overlapping labels, a peeled corner, tape over the top
  {
    const [x, y, w, h] = rect(DC.collage);
    ragRect(x + 14, y + 20, 120, 84, PAPER, 2); text('LOT 44', x + 74, y + 62, 26, INK, -0.05);
    ragRect(x + 110, y + 40, 120, 70, ORANGE, 2); text('50% OFF', x + 170, y + 76, 26, CREAM, 0.06);
    g.fillStyle = '#e3a21f'; g.beginPath(); g.arc(x + 70, y + 160, 50, 0, Math.PI * 2); g.fill();
    text('AS IS', x + 70, y + 160, 24, INK, -0.2);
    ragRect(x + 130, y + 130, 96, 100, '#d8ccac', 2);
    for (let i = 0; i < 9; i++) g.fillRect(x + 140 + i * 9, y + 150, 4 + (i % 3) * 2, 60);
    rag([[x + 226, y + 130], [x + 196, y + 130], [x + 226, y + 158]], '#a89f86', 1); // peeled corner
    ragRect(x + 40, y + 108, 150, 26, 'rgba(228,214,170,0.85)', 1.5);                 // tape
    scuff(x + 10, y + 10, w - 20, h - 20, 260);
  }
  // ---- masking tape strip with marker writing
  {
    const [x, y, w] = rect(DC.tape);
    ragRect(x + 6, y + 40, w - 12, 48, '#ddd0a4', 3);
    g.fillStyle = 'rgba(120,100,60,0.35)'; g.fillRect(x + 6, y + 40, 6, 48); g.fillRect(x + w - 12, y + 40, 6, 48);
    text('DO NOT RETURN', x + w / 2, y + 64, 26, INK, 0.02, 'bold italic');
  }
  // ---- stencil text (broken letter bridges)
  {
    const [x, y, w] = rect(DC.stencil);
    text('UNIT-07', x + w / 2, y + 64, 58, INK, 0, 'bold');
    g.fillStyle = 'rgba(0,0,0,1)'; g.globalCompositeOperation = 'destination-out';
    for (let i = 0; i < 7; i++) g.fillRect(x + 22 + i * 31, y + 58, 5, 12);
    g.globalCompositeOperation = 'source-over';
    scuff(x, y + 30, w, 70, 90, 'rgba(56,48,25,0.4)');
  }
  // ---- spray tag: a looping magenta signature with a halo of overspray
  {
    const [x, y] = rect(DC.spray);
    g.fillStyle = 'rgba(176,58,106,0.25)';
    for (let i = 0; i < 160; i++) { const a = rnd() * 6.28, r = 20 + rnd() * 40; g.fillRect(x + 64 + Math.cos(a) * r, y + 64 + Math.sin(a) * r * 0.6, 2, 2); }
    const pts: number[][] = [];
    for (let i = 0; i <= 40; i++) { const t = i / 40; pts.push([x + 14 + t * 100, y + 64 + Math.sin(t * 19) * 22 * (1 - t * 0.4)]); }
    stroke(pts, MAG, 8);
    stroke([[x + 20, y + 98], [x + 108, y + 92]], MAG, 5);
  }
  // print grain over everything that was painted (keeps the riso feel up close)
  {
    const id = g.getImageData(0, 0, S, S), d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] === 0) continue;
      const n = 0.9 + rnd() * 0.18;
      d[i] = Math.min(255, d[i] * n); d[i + 1] = Math.min(255, d[i + 1] * n); d[i + 2] = Math.min(255, d[i + 2] * n);
    }
    g.putImageData(id, 0, 0);
  }
  void MINT;
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.needsUpdate = true;
  atlas = t;
  return t;
}

/** atlas uv rect of a cell, as (u0, v0, u1, v1) with v up (flipY texture) */
export function cellUv(c: Cell): [number, number, number, number] {
  return [c[0] / 8, 1 - (c[1] + c[3]) / 8, (c[0] + c[2]) / 8, 1 - c[1] / 8];
}
